// "Your color": the six personal accents (slice 1c). Lives on the Plan
// Defaults page for now; slice 2 moves it behind the account menu. Settings
// as a whole are not designed yet, so this is deliberately plain.
import { useState } from "react";
import { ACCENT_LABELS, ACCENT_NAMES, COLORS, DEFAULT_ACCENT, FONT_SIZE, RADIUS, SPACE, type AccentName } from "../lib/constants";
import { isAccentName, saveAccent } from "../lib/theme";

type Status = "idle" | "saving" | "saved" | "error";

function currentAccent(): AccentName {
  const value = document.documentElement.dataset.accent;
  return isAccentName(value) ? value : DEFAULT_ACCENT;
}

export function AccentPicker() {
  const [accent, setAccent] = useState<AccentName>(currentAccent);
  const [status, setStatus] = useState<Status>("idle");

  const choose = (next: AccentName) => {
    if (next === accent) return;
    setAccent(next);
    setStatus("saving");
    saveAccent(next).then(
      () => setStatus("saved"),
      () => setStatus("error"),
    );
  };

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginTop: SPACE.x3, maxWidth: 560 }}>
      <div id="accent-picker-label" style={{ fontSize: FONT_SIZE.base, fontWeight: 700, marginBottom: SPACE.xs }}>
        Your color
      </div>
      <p className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: COLORS.soft, margin: "0 0 var(--space-xl2)" }}>
        Only you see this. It marks what's selected, today, and the main buttons.
      </p>
      <div role="radiogroup" aria-labelledby="accent-picker-label" className="mp-sans" style={{ display: "flex", flexWrap: "wrap", gap: SPACE.xl }}>
        {ACCENT_NAMES.map((name) => {
          const selected = name === accent;
          return (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => choose(name)}
              className="mp-btn"
              style={{ background: "transparent", padding: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: SPACE.xs, minWidth: 64 }}
            >
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: RADIUS.pill,
                  background: `var(--swatch-${name})`,
                  boxShadow: selected ? `0 0 0 3px ${COLORS.card}, 0 0 0 5px ${COLORS.ink}` : "none",
                }}
              />
              <span style={{ fontSize: FONT_SIZE.sm, color: selected ? COLORS.ink : COLORS.soft, fontWeight: selected ? 700 : 400 }}>{ACCENT_LABELS[name]}</span>
            </button>
          );
        })}
      </div>
      <p className="mp-sans" role="status" style={{ fontSize: FONT_SIZE.sm, color: status === "error" ? COLORS.danger : COLORS.soft, margin: "var(--space-lg) 0 0", minHeight: "1.4em" }}>
        {status === "saving" && "Saving…"}
        {status === "saved" && "Saved. It will follow you to your other devices."}
        {status === "error" && "Couldn't save to your account, so it's set on this device only. Try again later."}
      </p>
    </div>
  );
}
